<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import FilialCertificadoFrame from '@/components/organizacoes/FilialCertificadoFrame.vue'
import FilialProvedorFiscalFrame from '@/components/organizacoes/FilialProvedorFiscalFrame.vue'
import { parceiroArmazemSearch } from '@/search-definitions/parceiroArmazemSearch'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  listas: { type: Object, required: true },
  idOrganizacao: { type: Number, required: true }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const accordionState = reactive({
  identificacao: true,
  documentosFiscal: false,
  endereco: false,
  certificado: false,
  provedorFiscal: false,
  locaisEstoque: false
})

const { item, isLoading, isEditing, entityInfo, submitForm } = useFormulario(props, emit, {
  idKey: 'idFilial'
})

const listasDinamicas = reactive({})

// Garantir que a lista de locais de estoque sempre exista para evitar erros de 'push'
watch(item, (newVal) => {
  if (newVal && !newVal.locaisEstoque) {
    newVal.locaisEstoque = []
  }
}, { immediate: true })

const localEmEdicao = ref(null)
const showModalLocal = computed({
  get: () => !!localEmEdicao.value,
  set: (val) => { if (!val) localEmEdicao.value = null }
})
const tituloModalLocal = computed(() => {
  if (!localEmEdicao.value) return 'Manutenção de Local de Estoque'
  if (localEmEdicao.value._isNew) return 'Novo Local de Estoque'
  return `Local de Estoque #${localEmEdicao.value.idLocalEstoque}`
})

const mostrarAuditoria = ref(false)
const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['Filial', 'LocalEstoque'])
    Object.assign(listasDinamicas, result)
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares:', e)
  }
}

onMounted(async () => {
  await carregarListas()

  if (!props.itemParaEditar) {
    item.value = {
      idFilial: 0,
      idOrganizacao: props.idOrganizacao,
      idEmpresa: authStore.empresaAtiva,
      cnpj: '',
      inscricaoEstadual: '',
      lstStatusFilial: null,
      uf: '',
      codigoMunicipioIbge: '',
      identificacao: '',
      logradouro: '',
      cep: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      pais: 'Brasil',
      latitude: null,
      longitude: null,
      locaisEstoque: []
    }
  } else {
    await buscarLocaisEstoque()
  }
})

async function buscarLocaisEstoque () {
  if (!item.value.idFilial) return
  try {
    const res = await api.get(`/LocaisEstoque?idFilial=${item.value.idFilial}`)
    item.value.locaisEstoque = res.data
  } catch (error) {
    console.error('Erro ao buscar locais de estoque:', error)
    emit('show-notification', { message: 'Erro ao carregar locais de estoque.', type: 'error' })
  }
}

async function handleConfirmar () {
  if (!item.value.cnpj) {
    emit('show-notification', { message: 'O CNPJ da filial é obrigatório.', type: 'warning' })
    return
  }

  // Injetar IDs necessários
  item.value.idOrganizacao = props.idOrganizacao
  item.value.idEmpresa = authStore.empresaAtiva

  const sucesso = await submitForm('Filiais', 'idFilial', item.value)
  if (sucesso) {
    emit('show-notification', { message: 'Filial salva com sucesso!', type: 'success' })
    emit('item-atualizado', item.value)
  }
}

function handleCancelar () {
  emit('cancelar')
}

// --- Lógica de Locais de Estoque ---
function iniciarNovoLocal () {
  localEmEdicao.value = {
    _isNew: true,
    idLocalEstoque: 0,
    idFilial: item.value.idFilial,
    idEmpresa: authStore.empresaAtiva,
    descricao: '',
    lstTipoLocal: null,
    lstStatusLocalEstoque: null,
    isBloqueadoParaVenda: false,
    isBloqueadoEntrada: false,
    isBloqueadoSaida: false,
    isBloqueadoReserva: true,
    // --- Novos campos ---
    logradouro: '',
    cep: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    pais: 'Brasil',
    latitude: null,
    longitude: null,
    idParceiroArmazem: null,
    observacao: '',
    isEstoqueProprio: true
  }
  enderecoLocalBloqueado.value = true
}

function editarLocal (loc, lIdx) {
  localEmEdicao.value = JSON.parse(JSON.stringify({ ...loc, _lIdx: lIdx, _isNew: false }))
}

async function salvarLocalEstoque () {
  if (!localEmEdicao.value.descricao || !localEmEdicao.value.lstTipoLocal) {
    emit('show-notification', { message: 'Descrição e Tipo de Local são obrigatórios.', type: 'warning' })
    return
  }
  if (!localEmEdicao.value.lstStatusLocalEstoque) {
    emit('show-notification', { message: 'O status do local de estoque é obrigatório.', type: 'warning' })
    return
  }

  try {
    const payload = { ...localEmEdicao.value }
    payload.idFilial = item.value.idFilial
    payload.idEmpresa = authStore.empresaAtiva

    if (localEmEdicao.value._isNew) {
      await api.post('/LocaisEstoque', payload)
    } else {
      await api.put(`/LocaisEstoque/${payload.idLocalEstoque}`, payload)
    }

    emit('show-notification', { message: 'Local de estoque salvo com sucesso!', type: 'success' })
    await buscarLocaisEstoque()
    localEmEdicao.value = null
  } catch (error) {
    console.error('Erro ao salvar local de estoque:', error)
    emit('show-notification', { message: 'Erro ao salvar local de estoque.', type: 'error' })
  }
}

function getTipoLocalTexto (idTipo) {
  const tipoObj = (listasDinamicas.LstTipoLocal || []).find(t => String(t.idListaClienteItem) === String(idTipo))
  return tipoObj ? tipoObj.textoLista : idTipo
}

function getStatusLocalTexto (idStatus) {
  const statusObj = listasDinamicas.LstStatusLocalEstoque?.find(s => String(s.idListaClienteItem) === String(idStatus))
  return statusObj ? statusObj.textoLista : (idStatus ?? '-')
}

// --- Configuração da API de Geolocalização ---
const OPENCAGE_API_KEY = 'a0bf8e0cec934a608fc71997d036a80e'

// --- Controle de Bloqueio dos Campos de Endereço ---
// Nascem bloqueados (true); só liberam se o CEP não for encontrado (false)
const enderecoBloqueado = ref(true)

// --- Controle de Bloqueio dos Campos de Endereço do Local de Estoque ---
const enderecoLocalBloqueado = ref(true)

// --- Formatação e Busca de CEP ---
const formatarCep = (e) => {
  let v = e.target.value.replace(/\D/g, '')
  if (v.length > 5) v = v.replace(/^(\d{5})(\d)/, '$1-$2')
  item.value.cep = v
}

async function buscarCep () {
  const cep = item.value.cep?.replace(/\D/g, '')
  if (cep && cep.length === 8) {
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      const data = await res.json()

      if (!data.erro) {
        enderecoBloqueado.value = true
        item.value.logradouro = data.logradouro
        item.value.bairro = data.bairro
        item.value.cidade = data.localidade
        item.value.uf = data.uf
        item.value.pais = 'Brasil'

        buscarCoordenadas()

        setTimeout(() => {
          const elNum = document.getElementById('numero')
          if (elNum) elNum.focus()
        }, 100)
      } else {
        enderecoBloqueado.value = false
        emit('show-notification', { message: 'CEP não encontrado na base dos Correios. Preencha os campos manualmente.', type: 'warning' })
      }
    } catch (error) {
      enderecoBloqueado.value = false
      console.warn('Erro ao buscar CEP', error)
      emit('show-notification', { message: 'Falha de comunicação ao buscar o CEP. Preencha os campos manualmente.', type: 'error' })
    }
  } else if (cep && cep.length > 0 && cep.length < 8) {
    emit('show-notification', { message: 'CEP inválido. Digite os 8 números.', type: 'warning' })
  }
}

async function buscarCoordenadas () {
  if (!item.value.logradouro || !item.value.cidade) {
    console.warn('[buscarCoordenadas] Logradouro ou cidade ausentes — geocoding ignorado.')
    return
  }
  if (!OPENCAGE_API_KEY || OPENCAGE_API_KEY === 'SUA_CHAVE_OPENCAGE_AQUI') {
    console.warn('[buscarCoordenadas] Chave OpenCage não configurada — geocoding ignorado.')
    return
  }

  try {
    let query = `${item.value.logradouro}`
    if (item.value.numero) query += `, ${item.value.numero}`
    query += `, ${item.value.cidade}, ${item.value.uf}`
    if (item.value.pais) query += `, ${item.value.pais}`

    const url = `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(query)}&key=${OPENCAGE_API_KEY}`
    const res = await fetch(url)

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
    const data = await res.json()

    if (data && data.results && data.results.length > 0) {
      item.value.latitude = data.results[0].geometry.lat
      item.value.longitude = data.results[0].geometry.lng
    }
  } catch (error) {
    console.warn('Erro ao buscar coordenadas:', error)
  }
}

// --- Formatação e Busca de CEP para Local de Estoque ---
const formatarCepLocal = (e) => {
  let v = e.target.value.replace(/\D/g, '')
  if (v.length > 5) v = v.replace(/^(\d{5})(\d)/, '$1-$2')
  localEmEdicao.value.cep = v
}

async function buscarCepLocal () {
  const cep = localEmEdicao.value?.cep?.replace(/\D/g, '')
  if (cep && cep.length === 8) {
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      const data = await res.json()
      if (!data.erro) {
        enderecoLocalBloqueado.value = true
        localEmEdicao.value.logradouro = data.logradouro
        localEmEdicao.value.bairro = data.bairro
        localEmEdicao.value.cidade = data.localidade
        localEmEdicao.value.uf = data.uf
        localEmEdicao.value.pais = 'Brasil'
        buscarCoordenadasLocal()
        setTimeout(() => {
          const elNum = document.getElementById('local-estoque-numero')
          if (elNum) elNum.focus()
        }, 100)
      } else {
        enderecoLocalBloqueado.value = false
        emit('show-notification', { message: 'CEP não encontrado na base dos Correios. Preencha os campos manualmente.', type: 'warning' })
      }
    } catch (error) {
      enderecoLocalBloqueado.value = false
      console.warn('Erro ao buscar CEP do local', error)
      emit('show-notification', { message: 'Falha de comunicação ao buscar o CEP. Preencha os campos manualmente.', type: 'error' })
    }
  } else if (cep && cep.length > 0 && cep.length < 8) {
    emit('show-notification', { message: 'CEP inválido. Digite os 8 números.', type: 'warning' })
  }
}

async function buscarCoordenadasLocal () {
  if (!localEmEdicao.value?.logradouro || !localEmEdicao.value?.cidade) return
  const OPENCAGE_API_KEY = 'a0bf8e0cec934a608fc71997d036a80e'
  try {
    let query = `${localEmEdicao.value.logradouro}`
    if (localEmEdicao.value.numero) query += `, ${localEmEdicao.value.numero}`
    query += `, ${localEmEdicao.value.cidade}, ${localEmEdicao.value.uf}`
    if (localEmEdicao.value.pais) query += `, ${localEmEdicao.value.pais}`
    const url = `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(query)}&key=${OPENCAGE_API_KEY}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
    const data = await res.json()
    if (data && data.results && data.results.length > 0) {
      localEmEdicao.value.latitude = data.results[0].geometry.lat
      localEmEdicao.value.longitude = data.results[0].geometry.lng
    }
  } catch (error) {
    console.warn('Erro ao buscar coordenadas do local:', error)
  }
}

function copiarEnderecoFilial () {
  if (!item.value) return
  localEmEdicao.value.logradouro = item.value.logradouro || ''
  localEmEdicao.value.cep = item.value.cep || ''
  localEmEdicao.value.numero = item.value.numero || ''
  localEmEdicao.value.complemento = item.value.complemento || ''
  localEmEdicao.value.bairro = item.value.bairro || ''
  localEmEdicao.value.cidade = item.value.cidade || ''
  localEmEdicao.value.uf = item.value.uf || ''
  localEmEdicao.value.pais = item.value.pais || 'Brasil'
  localEmEdicao.value.latitude = item.value.latitude || null
  localEmEdicao.value.longitude = item.value.longitude || null
  enderecoLocalBloqueado.value = true
}
</script>

<template>
  <FormularioCadastro
    module-name="Filial"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :accordion-state="accordionState"
    @submit="handleConfirmar"
    @cancelar="handleCancelar"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div class="row g-3">
      <!-- IDENTIFICAÇÃO -->
      <div class="col-12">
        <div class="card">
          <div
            class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.identificacao = !accordionState.identificacao"
          >
            <h5 class="mb-0"><i class="bi bi-tag me-2"></i>Identificação</h5>
            <i class="bi" :class="accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div v-show="accordionState.identificacao" class="card-body">
            <div class="row g-3">
              <div class="col-md-8">
                <CampoInput id="identificacao" label="Identificação (ex: Sede, Filial 1)" v-model="item.identificacao" />
              </div>
              <div class="col-md-4">
                <CampoSelect
                  id="lstStatusFilial"
                  label="Status da Filial"
                  v-model="item.lstStatusFilial"
                  :options="listasDinamicas.LstStatusFilial || []"
                  option-value="idListaClienteItem"
                  option-label="textoLista"
                />
              </div>
              <div class="col-md-4">
                <CampoSelect
                  id="indicadorAtividade"
                  label="Indicador de Atividade (SPED)"
                  v-model="item.indicadorAtividade"
                  :options="[{ id: '0', label: 'Industrial ou equiparado' }, { id: '1', label: 'Outros' }]"
                  option-value="id"
                  option-label="label"
                >
                  <template #label>
                    Indicador de Atividade (SPED)
                    <i class="bi bi-info-circle ms-1" title="Campo IND_ATIV do registro 0000 da EFD ICMS/IPI."></i>
                  </template>
                </CampoSelect>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- DOCUMENTOS E FISCAL -->
      <div class="col-12">
        <div class="card">
          <div
            class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.documentosFiscal = !accordionState.documentosFiscal"
          >
            <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Documentos e Fiscal</h5>
            <i class="bi" :class="accordionState.documentosFiscal ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div v-show="accordionState.documentosFiscal" class="card-body">
            <div class="row g-3">
              <div class="col-md-4">
                <CampoInput id="cnpj" label="CNPJ" v-model="item.cnpj" required />
              </div>
              <div class="col-md-4">
                <CampoInput id="inscricaoEstadual" label="Inscrição Estadual" v-model="item.inscricaoEstadual" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ENDEREÇO -->
      <div class="col-12">
        <div class="card">
          <div
            class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.endereco = !accordionState.endereco"
          >
            <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Localização / Endereço</h5>
            <i class="bi" :class="accordionState.endereco ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div v-show="accordionState.endereco" class="card-body">
            <div class="row g-3">
              <!-- CEP primeiro — condiciona os demais campos -->
              <div class="col-md-4">
                <label class="form-label">CEP <span class="text-danger">*</span></label>
                <input type="text" id="cep" class="form-control" v-model="item.cep" maxlength="9" placeholder="00000-000" @input="formatarCep" @blur="buscarCep">
                <small class="text-muted d-block mt-1">Preencha o CEP para buscar o endereço automaticamente.</small>
              </div>
              <div class="col-md-8">
                <CampoInput id="logradouro" label="Logradouro" v-model="item.logradouro" :disabled="enderecoBloqueado" />
              </div>
              <div class="col-md-2">
                <CampoInput id="numero" label="Número" v-model="item.numero" @blur="buscarCoordenadas" />
              </div>
              <div class="col-md-4">
                <CampoInput id="complemento" label="Complemento" v-model="item.complemento" />
              </div>
              <div class="col-md-4">
                <CampoInput id="bairro" label="Bairro" v-model="item.bairro" :disabled="enderecoBloqueado" />
              </div>
              <div class="col-md-4">
                <CampoInput id="cidade" label="Cidade" v-model="item.cidade" :disabled="enderecoBloqueado" />
              </div>
              <div class="col-md-2">
                <CampoInput id="uf" label="UF" v-model="item.uf" maxlength="2" :disabled="enderecoBloqueado" />
              </div>
              <div class="col-md-3">
                <CampoInput id="codigoMunicipioIbge" label="Cód. IBGE" v-model="item.codigoMunicipioIbge" maxlength="7" />
              </div>
              <div class="col-md-3">
                <CampoInput id="pais" label="País" v-model="item.pais" :disabled="enderecoBloqueado" />
              </div>
            </div>
            <div v-if="item.latitude || item.longitude" class="row g-3 mt-2">
              <div class="col-12">
                <div class="p-2 border border-secondary-subtle rounded bg-body-tertiary d-flex gap-4">
                  <span class="small text-body-secondary"><i class="bi bi-geo-alt-fill me-1"></i> Latitude: <strong class="text-body">{{ item.latitude }}</strong></span>
                  <span class="small text-body-secondary">Longitude: <strong class="text-body">{{ item.longitude }}</strong></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CERTIFICADO DIGITAL -->
      <div class="col-12">
        <div class="card">
          <div
            class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.certificado = !accordionState.certificado"
          >
            <h5 class="mb-0"><i class="bi bi-shield-lock me-2"></i>Certificado Digital</h5>
            <i class="bi" :class="accordionState.certificado ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div v-show="accordionState.certificado" class="card-body">
            <FilialCertificadoFrame
              :id-filial="item.idFilial || 0"
              @show-notification="(p) => emit('show-notification', p)"
            />
          </div>
        </div>
      </div>

      <!-- CREDENCIAIS DE EMISSÃO FISCAL (PROVEDOR) -->
      <div class="col-12">
        <div class="card">
          <div
            class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.provedorFiscal = !accordionState.provedorFiscal"
          >
            <h5 class="mb-0"><i class="bi bi-plug me-2"></i>Credenciais de Emissão Fiscal</h5>
            <i class="bi" :class="accordionState.provedorFiscal ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div v-show="accordionState.provedorFiscal" class="card-body">
            <FilialProvedorFiscalFrame
              :id-filial="item.idFilial || 0"
              @show-notification="(p) => emit('show-notification', p)"
            />
          </div>
        </div>
      </div>

      <!-- LOCAIS DE ESTOQUE -->
      <div class="col-12">
        <div class="card border-primary">
          <div
            class="card-header bg-primary text-white cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.locaisEstoque = !accordionState.locaisEstoque"
          >
            <h5 class="mb-0 text-white"><i class="bi bi-boxes me-2"></i>Locais de Estoque</h5>
            <div class="d-flex align-items-center gap-2">
              <button
                type="button"
                class="btn btn-sm btn-light py-0"
                @click.stop="iniciarNovoLocal"
                :disabled="!item.idFilial"
                :title="!item.idFilial ? 'Salve a filial primeiro para adicionar locais de estoque' : 'Adicionar Local'"
              >
                <i class="bi bi-plus-lg me-1"></i> Adicionar Local
              </button>
              <i class="bi text-white" :class="accordionState.locaisEstoque ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.locaisEstoque" class="card-body p-0">
            <div class="admin-table-container border-0 shadow-none">
              <table class="table admin-table table-sm m-0">
                <thead>
                  <tr>
                    <th style="width: 80px;">ID</th>
                    <th>Descrição</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th style="width: 100px;">Cidade/UF</th>
                    <th style="width: 100px;">Tipo</th>
                    <th style="width: 110px;">Bloq. Venda</th>
                    <th style="width: 110px;">Bloq. Entrada</th>
                    <th style="width: 110px;">Bloq. Saída</th>
                    <th style="width: 110px;">Bloq. Reserva</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(loc, lIdx) in item.locaisEstoque" :key="lIdx">
                    <td class="ps-3">{{ loc.idLocalEstoque }}</td>
                    <td>{{ loc.descricao }}</td>
                    <td>{{ getTipoLocalTexto(loc.lstTipoLocal) }}</td>
                    <td>{{ getStatusLocalTexto(loc.lstStatusLocalEstoque) }}</td>
                    <td>{{ loc.cidade || '-' }}/{{ loc.uf || '' }}</td>
                    <td>
                      <span v-if="loc.isEstoqueProprio" class="badge bg-success-subtle text-success border border-success-subtle">Próprio</span>
                      <span v-else class="badge bg-warning-subtle text-warning border border-warning-subtle">Armazém</span>
                    </td>
                    <td class="text-center">
                      <span v-if="loc.isBloqueadoParaVenda" class="badge bg-danger-subtle text-danger border border-danger-subtle">Sim</span>
                      <span v-else class="badge bg-success-subtle text-success border border-success-subtle">Não</span>
                    </td>
                    <td class="text-center">
                      <span v-if="loc.isBloqueadoEntrada" class="badge bg-danger-subtle text-danger border border-danger-subtle">Sim</span>
                      <span v-else class="badge bg-success-subtle text-success border border-success-subtle">Não</span>
                    </td>
                    <td class="text-center">
                      <span v-if="loc.isBloqueadoSaida" class="badge bg-danger-subtle text-danger border border-danger-subtle">Sim</span>
                      <span v-else class="badge bg-success-subtle text-success border border-success-subtle">Não</span>
                    </td>
                    <td class="text-center">
                      <span v-if="loc.isBloqueadoReserva" class="badge bg-danger-subtle text-danger border border-danger-subtle">Sim</span>
                      <span v-else class="badge bg-success-subtle text-success border border-success-subtle">Não</span>
                    </td>
                    <td class="acoes-cell text-end pe-3">
                      <button type="button" class="btn-icon p-1" title="Editar" @click="editarLocal(loc, lIdx)"><i class="bi bi-pencil-fill"></i></button>
                    </td>
                  </tr>
                  <tr v-if="!item.locaisEstoque || item.locaisEstoque.length === 0">
                    <td colspan="11" class="text-center py-4 text-muted">Nenhum local de estoque cadastrado para esta filial.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE LOCAL DE ESTOQUE -->
    <ModalFormulario v-model="showModalLocal" :titulo="tituloModalLocal" tamanho="xl" @confirmar="salvarLocalEstoque">
      <div v-if="localEmEdicao" class="row g-3">

        <!-- ===== FRAME 1: DADOS BÁSICOS ===== -->
        <div class="col-12 col-xl-4">
          <div class="card h-100">
            <div class="card-header bg-body-tertiary py-2">
              <h6 class="mb-0 fw-semibold"><i class="bi bi-info-circle me-1"></i> Dados Básicos</h6>
            </div>
            <div class="card-body">
              <div class="row g-2">
                <div class="col-12">
                  <CampoInput id="descricao" label="Descrição do Local" v-model="localEmEdicao.descricao" required />
                </div>
                <div class="col-12">
                  <CampoBuscaLista
                    id="lstTipoLocal"
                    label="Tipo de Local"
                    v-model="localEmEdicao.lstTipoLocal"
                    tabela="LocalEstoque"
                    required
                  />
                </div>
                <div class="col-12">
                  <CampoSelect
                    id="local-estoque-is-estoque-proprio"
                    label="Estoque Próprio?"
                    v-model="localEmEdicao.isEstoqueProprio"
                    :options="[{id: true, label: 'Sim'}, {id: false, label: 'Não'}]"
                    option-value="id"
                    option-label="label"
                  />
                </div>
                <div class="col-12">
                  <CampoBusca
                    id="local-estoque-id-parceiro-armazem"
                    label="Fornecedor do Armazém"
                    v-model="localEmEdicao.idParceiroArmazem"
                    :configuracao-busca="parceiroArmazemSearch"
                    :disabled="localEmEdicao.isEstoqueProprio"
                  />
                </div>
                <div class="col-12">
                  <div class="mb-2">
                    <label class="form-label" for="local-estoque-observacao">Observação</label>
                    <textarea id="local-estoque-observacao" class="form-control" v-model="localEmEdicao.observacao" rows="2" maxlength="500"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== FRAME 2: FUNCIONAMENTO DO ESTOQUE ===== -->
        <div class="col-12 col-xl-4">
          <div class="card h-100">
            <div class="card-header bg-body-tertiary py-2">
              <h6 class="mb-0 fw-semibold"><i class="bi bi-gear me-1"></i> Funcionamento do Estoque</h6>
            </div>
            <div class="card-body">
              <div class="row g-2">
                <div class="col-12">
                  <CampoSelect
                    id="isBloqueadoParaVenda"
                    label="Bloqueado para Venda?"
                    v-model="localEmEdicao.isBloqueadoParaVenda"
                    :options="[{id: true, label: 'Sim'}, {id: false, label: 'Não'}]"
                    option-value="id"
                    option-label="label"
                  />
                </div>
                <div class="col-12">
                  <CampoSelect
                    id="isBloqueadoEntrada"
                    label="Bloqueado para Entrada?"
                    v-model="localEmEdicao.isBloqueadoEntrada"
                    :options="[{id: true, label: 'Sim'}, {id: false, label: 'Não'}]"
                    option-value="id"
                    option-label="label"
                  />
                </div>
                <div class="col-12">
                  <CampoSelect
                    id="isBloqueadoSaida"
                    label="Bloqueado para Saída?"
                    v-model="localEmEdicao.isBloqueadoSaida"
                    :options="[{id: true, label: 'Sim'}, {id: false, label: 'Não'}]"
                    option-value="id"
                    option-label="label"
                  />
                </div>
                <div class="col-12">
                  <CampoSelect
                    id="isBloqueadoReserva"
                    label="Bloqueado para Reserva?"
                    v-model="localEmEdicao.isBloqueadoReserva"
                    :options="[{id: true, label: 'Sim'}, {id: false, label: 'Não'}]"
                    option-value="id"
                    option-label="label"
                  />
                </div>
                <div class="col-12">
                  <CampoSelect
                    id="lstStatusLocalEstoque"
                    label="Status"
                    v-model="localEmEdicao.lstStatusLocalEstoque"
                    :options="listasDinamicas.LstStatusLocalEstoque || []"
                    option-value="idListaClienteItem"
                    option-label="textoLista"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== FRAME 3: ENDEREÇO ===== -->
        <div class="col-12 col-xl-4">
          <div class="card h-100">
            <div class="card-header bg-body-tertiary py-2">
              <h6 class="mb-0 fw-semibold"><i class="bi bi-geo-alt me-1"></i> Endereço</h6>
            </div>
            <div class="card-body">
              <div class="row g-2">
                <!-- Botão Copiar Endereço — sempre visível, disabled se não for próprio -->
                <div class="col-12">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm w-100"
                    @click="copiarEnderecoFilial"
                    :disabled="!localEmEdicao.isEstoqueProprio"
                  >
                    <i class="bi bi-copy me-1"></i> Copiar Endereço da Filial
                  </button>
                </div>
                <!-- CEP -->
                <div class="col-12">
                  <label class="form-label">CEP</label>
                  <input type="text" id="local-estoque-cep" class="form-control" v-model="localEmEdicao.cep" maxlength="9" placeholder="00000-000" @input="formatarCepLocal" @blur="buscarCepLocal">
                </div>
                <!-- Logradouro -->
                <div class="col-12">
                  <CampoInput id="local-estoque-logradouro" label="Logradouro" v-model="localEmEdicao.logradouro" :disabled="enderecoLocalBloqueado" />
                </div>
                <!-- Número + Complemento -->
                <div class="col-5">
                  <CampoInput id="local-estoque-numero" label="Número" v-model="localEmEdicao.numero" @blur="buscarCoordenadasLocal" />
                </div>
                <div class="col-7">
                  <CampoInput id="local-estoque-complemento" label="Complemento" v-model="localEmEdicao.complemento" />
                </div>
                <!-- Bairro -->
                <div class="col-12">
                  <CampoInput id="local-estoque-bairro" label="Bairro" v-model="localEmEdicao.bairro" :disabled="enderecoLocalBloqueado" />
                </div>
                <!-- Cidade + UF -->
                <div class="col-8">
                  <CampoInput id="local-estoque-cidade" label="Cidade" v-model="localEmEdicao.cidade" :disabled="enderecoLocalBloqueado" />
                </div>
                <div class="col-4">
                  <CampoInput id="local-estoque-uf" label="UF" v-model="localEmEdicao.uf" maxlength="2" :disabled="enderecoLocalBloqueado" />
                </div>
                <!-- País -->
                <div class="col-12">
                  <CampoInput id="local-estoque-pais" label="País" v-model="localEmEdicao.pais" :disabled="enderecoLocalBloqueado" />
                </div>
                <!-- Coordenadas (se houver) -->
                <div v-if="localEmEdicao.latitude || localEmEdicao.longitude" class="col-12">
                  <div class="p-1 border border-secondary-subtle rounded bg-body-tertiary d-flex gap-2" style="font-size:0.8rem;">
                    <span class="text-body-secondary"><i class="bi bi-geo-alt-fill me-1"></i> Lat: <strong>{{ localEmEdicao.latitude }}</strong></span>
                    <span class="text-body-secondary">Lon: <strong>{{ localEmEdicao.longitude }}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </ModalFormulario>

    <AuditLogViewer
      v-if="mostrarAuditoria"
      tabela="Filial"
      :chave-primaria="item.idFilial"
      :titulo-registro="item.identificacao || item.cnpj || 'Filial'"
      :tabelas-filhas="['LocalEstoque']"
      chave-estrangeira="IdFilial"
      :dicionarios="{
        'lstStatusFilial': listasDinamicas.LstStatusFilial || []
      }"
      @fechar="mostrarAuditoria = false"
    />
  </FormularioCadastro>
</template>

<style scoped>
.admin-table-container {
  background-color: transparent;
  overflow-x: auto;
}
.btn-icon-minimalista { background: none; border: none; color: var(--bs-body-color); cursor: pointer; font-size: 1.3rem; transition: 0.2s; }
.btn-icon-minimalista:hover { color: var(--bs-body-emphasis); }
.btn-icon { background: none; border: none; color: var(--bs-secondary-color); padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: var(--bs-body-emphasis); }
.cursor-pointer { cursor: pointer; }
</style>
